import pdfMake from 'src/modules/shared/pdf/pdfMakeClient';
import {i18n} from 'src/i18n';
import SettingsService from 'src/modules/settings/settingsService';
import {isPlastyvergTenant} from 'src/modules/tarea/dynamicSubsectionPdfGrouping';
import {helveticaStringWidth, helveticaWidestWord} from 'src/modules/tarea/helveticaStringWidth';
import {
  findPrimeroDelParcialId23Task,
  formatMedicionesParcialValue,
  MedicionesParcialId23,
  shouldUseMedicionesParcialId23,
} from 'src/modules/tarea/medicionesParcialId23';
import {
  CONTROL_VISUAL_GOMA_TEFLON_OPTIONS,
  findPrimeroDelParcialId24Task,
  hasMedicionesParcialCheckbox,
  MedicionesParcialId24,
  shouldUseMedicionesParcialId24,
  TIPO_EMBALAJE_OPTIONS,
} from 'src/modules/tarea/medicionesParcialId24';
import {
  buildParentAnswerPdfGrid,
  ParentFieldLayoutConfig,
  ParentFieldLayoutItem,
  remapPlastyvergParentFieldLabel,
} from 'src/modules/tarea/parentFieldLayout';
import {
  getPlastyvergParcialCriteriaItems,
  ParcialCriteriaItem,
  resolveParcialCriteriaTitulo,
} from 'src/modules/tarea/plastyvergParcialCriteria';
import {PlastyvergPdfHeaderCodeMeta, resolvePlastyvergPdfHeaderTitle} from 'src/modules/tarea/plastyvergPdfHeaderMeta';
import {
  buildParcialColumns,
  buildSubsectionHeaderSpans,
  CHILD_TASKS_WIDE_TABLE_COLUMN_THRESHOLD,
  formatProcedureAnswerText,
  getExecutorSignatureColumnLabel,
  getParcialAnswerText,
  getRecordId,
  getTaskDisplayNumber,
  groupTasksByModelo,
  hasSubsectionHeaderSpans,
  isChildTasksWideTableMode,
  shouldApplyPartialPdfFormatChanges,
  shouldUseSequentialChildTableRowNumbers,
} from 'src/modules/tarea/tareaParcialHelpers';
import {getTaskReportFolio} from 'src/modules/tarea/taskVisualLabel';
import TenantService from 'src/modules/tenant/tenantService';

/** En modo wide, tablas con esta cantidad de columnas o menos van lado a lado. */
const SIDE_BY_SIDE_MAX_COLUMNS = 6;

/** Dimensiones pdfMake en puntos (ancho × alto en portrait). */
const PDF_PAGE_SIZE_POINTS: Record<string, {width: number; height: number}> = {
  A4: {width: 595.28, height: 841.89},
  A3: {width: 841.89, height: 1190.55},
  A2: {width: 1190.55, height: 1683.78},
  A1: {width: 1683.78, height: 2383.94},
  A0: {width: 2383.94, height: 3370.39},
  // Chile: Carta = Letter 8.5×11 in; Oficio = 8.5×13 in (~216×330 mm).
  CARTA: {width: 612, height: 792},
  OFICIO: {width: 612, height: 936},
};

type ChildTasksPdfPageSize = keyof typeof PDF_PAGE_SIZE_POINTS;

/** Solo Plastyverg. Probar: 'A4' | 'CARTA' | 'OFICIO' | 'A3'. */
const PAGE_SIZE_PLASTYVERG: ChildTasksPdfPageSize = 'CARTA';

/** pdfMake no conoce CARTA/OFICIO; esos van como {width, height}. */
const toPdfMakePageSize = (pageSize: ChildTasksPdfPageSize) =>
  pageSize === 'CARTA' || pageSize === 'OFICIO' ? PDF_PAGE_SIZE_POINTS[pageSize] : pageSize;

const getPageDimensions = (pageSize: ChildTasksPdfPageSize, pageOrientation: 'portrait' | 'landscape') => {
  const size = PDF_PAGE_SIZE_POINTS[pageSize] || PDF_PAGE_SIZE_POINTS.A4;
  if (pageOrientation === 'landscape') {
    return {width: size.height, height: size.width};
  }
  return size;
};

/** Descarga una imagen (firma) y la convierte a dataURL para pdfMake. */
const fetchImageAsDataUrl = async (url): Promise<string | null> => {
  if (!url) return null;
  if (typeof url === 'string' && url.startsWith('data:')) return url;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return null;
  }
};

const getQuestionRawLabel = (item: any, fallback: string) =>
  item?.pregunta?.label || item?.pregunta?.titulo || item?.label || item?.titulo || fallback;

const getQuestionLabel = (item: any, fallback: string) => remapPlastyvergParentFieldLabel(getQuestionRawLabel(item, fallback));

const getQuestionKey = (item: any, index: number) =>
  item?.pregunta?.id || item?.id || `${getQuestionRawLabel(item, 'pregunta')}-${item?.tipoRespuesta || ''}-${index}`;

const getProcedureQuestions = (task: any) =>
  (task?.listaProcedimiento || [])
    .map((item, index) => ({item, index}))
    .filter(({item}) => item?.tipo === 'pregunta' || item?.pregunta)
    .map(({item, index}) => ({
      key: getQuestionKey(item, index),
      label: getQuestionLabel(item, i18n('entities.firma.parcial.blankQuestion')),
      answer: formatProcedureAnswerText(item, '-'),
    }));

const getSignatureSource = (signature: any) =>
  signature?.firma || signature?.firmaRef?.downloadUrl || signature?.firmaRef?.publicUrl || '';

const getExecutorSignature = (task: any) => {
  const executorId = getRecordId(task?.ejecutor);
  const signed = (task?.firmas || []).filter((signature) => signature?.firmada && getSignatureSource(signature));
  return signed.find((signature) => executorId && getRecordId(signature?.responsable) === executorId) || null;
};

const CHILD_TASKS_TABLE_FONT_SIZE = 7;

const buildParentAnswerCard = (question: {label: string; answer: string}) => ({
  stack: [
    {text: question.label, fontSize: 6.5, color: '#666666', margin: [0, 0, 0, 2]},
    // Plantilla en blanco: conservar label, valor vacío.
    {text: question.answer, fontSize: CHILD_TASKS_TABLE_FONT_SIZE},
  ],
  margin: [4, 4, 4, 4],
});

const buildParentAnswerGrid = (parentTask: any, layoutRaw: string[] | ParentFieldLayoutItem[] | ParentFieldLayoutConfig) =>
  buildParentAnswerPdfGrid(getProcedureQuestions(parentTask), layoutRaw, {
    whenEmptyUseAll: true,
    blankAnswers: true,
    buildCard: buildParentAnswerCard,
  });

const PDF_TABLE_BORDER_COLOR = '#d0d0d0';

const parentAnswersTableLayout = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => PDF_TABLE_BORDER_COLOR,
  vLineColor: () => PDF_TABLE_BORDER_COLOR,
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0,
};

/** Espacio horizontal entre columnas de mediciones (pt). Subir para más aire; 4 era el original. */
const MEDICIONES_COLUMN_GAP = 14;
/** Espacio vertical entre filas de mediciones (pt). Solo entre filas, no al borde. */
const MEDICIONES_ROW_GAP = 8;

const medicionesInnerGridLayout = {
  hLineWidth: () => 0,
  vLineWidth: () => 0,
  paddingLeft: () => 0,
  paddingRight: (i, node) => (i === node.table.widths.length - 1 ? 0 : MEDICIONES_COLUMN_GAP),
  paddingTop: () => 0,
  paddingBottom: (i, node) => (i === node.table.body.length - 1 ? 0 : MEDICIONES_ROW_GAP),
};

const wrapMedicionesPdfSection = (block: any) => ({
  unbreakable: true,
  table: {
    widths: ['*'],
    body: [[block]],
  },
  layout: parentAnswersTableLayout,
  margin: [0, 12, 0, 0],
});

const fetchTenantLogo = async () => {
  const [tenant, settings] = await Promise.all([TenantService.findCurrent(), SettingsService.find()]);
  const logoUrl = settings?.logosPdf?.[0]?.publicUrl || settings?.logos?.[0]?.publicUrl;
  const logo = logoUrl ? await fetchImageAsDataUrl(logoUrl) : null;
  return {tenant, logo};
};

const BLANK_PDF_FIXED_DATE = '29-07-2026';
const BLANK_PDF_VERSION = '12';

const buildHeaderDateCard = (date: string, verticalMargin: number) => ({
  stack: [
    {text: i18n('common.date'), fontSize: 6.5, color: '#666666', margin: [0, 0, 0, 2], alignment: 'left'},
    {text: date, fontSize: CHILD_TASKS_TABLE_FONT_SIZE, alignment: 'right'},
    {text: 'Version', fontSize: 6.5, color: '#666666', margin: [0, 4, 0, 2], alignment: 'left'},
    {text: BLANK_PDF_VERSION, fontSize: CHILD_TASKS_TABLE_FONT_SIZE, alignment: 'left'},
  ],
  margin: [4, verticalMargin, 14, verticalMargin],
});

/** Cabecera derecha Plastyverg: etiquetas y valores en columnas separadas. */
const buildPlastyvergHeaderCodeCard = (meta: PlastyvergPdfHeaderCodeMeta, verticalMargin: number) => {
  const labelStyle = {fontSize: 6.5, color: '#666666'};
  const valueStyle = {fontSize: CHILD_TASKS_TABLE_FONT_SIZE};
  const row = (label: string, value: string) => [
    {text: label, ...labelStyle, margin: [0, 1, 2, 1]},
    {text: `: ${value}`, ...valueStyle, margin: [0, 1, 0, 1]},
  ];

  return {
    table: {
      widths: ['auto', 'auto'],
      body: [row('Código', meta.codigo), row('Versión', meta.version), row('Fecha', meta.fechaVersion)],
    },
    layout: 'noBorders',
    margin: [4, verticalMargin, 10, verticalMargin],
  };
};

/** Alto mínimo de cabecera. La fila crece si logo/contenido lo requieren. */
const PDF_HEADER_ROW_MIN_HEIGHT = Math.round(20);
const PAGE_MARGINS = {top: 70, bottom: 32, left: 18, right: 18};
/** Padding interno de la cabecera repetida (vive en el margen top). */
const PAGE_HEADER_MARGIN = {top: 8, bottom: 6};
/** Padding interno del pie de página (vive en el margen bottom). */
const PAGE_FOOTER_MARGIN = {top: 16, bottom: 2};

export type ChildTasksTablePdfParcialSource = {
  /** Firma parcial explícita (p. ej. export desde un parcial seleccionado). */
  parcial?: any | null;
  /** Parciales del padre; si no hay `parcial`, se usa la última firmada. */
  parciales?: any[];
};

const buildMedicionesPdfValueCell = (value: unknown) => ({
  text: formatMedicionesParcialValue(value),
  fontSize: CHILD_TASKS_TABLE_FONT_SIZE,
  alignment: 'center' as const,
  margin: [2, 3, 2, 3],
});

const buildMedicionesPdfLabelCell = (label: string) => ({
  text: label,
  fontSize: CHILD_TASKS_TABLE_FONT_SIZE,
  bold: true,
  margin: [2, 3, 2, 3],
});

const formatMedicionesPdfTitleText = (title: string) => String(title || '').replace(/:\s*$/, '');

const medicionesNoBorderHeaderLayout = {
  hLineWidth: () => 0,
  vLineWidth: () => 0,
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0,
};

const buildMedicionesPdfTitle = (title: string, subtitle?: string) => ({
  stack: [
    {text: formatMedicionesPdfTitleText(title), fontSize: 7, bold: true, margin: [0, 0, 0, subtitle ? 1 : 3]},
    ...(subtitle ? [{text: subtitle, fontSize: 6, color: '#666666', margin: [0, 0, 0, 3]}] : []),
  ],
});

const buildMedicionesParcialId23PdfBlock = (_mediciones?: MedicionesParcialId23 | null) => {
  // Plantilla en blanco: mismos labels, valores vacíos.
  const blank = ' ';

  const termocontraccionBlock = {
    stack: [
      buildMedicionesPdfTitle(i18n('entities.tarea.medicionesParcial.termocontraccion')),
      {
        table: {
          widths: [28, '*'],
          body: [
            [
              buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.termocontraccionT')),
              buildMedicionesPdfValueCell(blank),
            ],
            [
              buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.termocontraccionL')),
              buildMedicionesPdfValueCell(blank),
            ],
          ],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  };

  const cofWidths = [42, '*', '*'];
  const cofBlock = {
    stack: [
      {
        table: {
          widths: cofWidths,
          body: [
            [
              {
                text: formatMedicionesPdfTitleText(i18n('entities.tarea.medicionesParcial.cof')),
                fontSize: 7,
                bold: true,
                margin: [0, 0, 0, 3],
              },
              {
                text: i18n('entities.tarea.medicionesParcial.estatico'),
                fontSize: 7,
                bold: true,
                alignment: 'center',
                margin: [0, 0, 0, 3],
              },
              {
                text: i18n('entities.tarea.medicionesParcial.dinamico'),
                fontSize: 7,
                bold: true,
                alignment: 'center',
                margin: [0, 0, 0, 3],
              },
            ],
          ],
        },
        layout: medicionesNoBorderHeaderLayout,
      },
      {
        table: {
          widths: cofWidths,
          body: [
            [
              buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.interno')),
              buildMedicionesPdfValueCell(blank),
              buildMedicionesPdfValueCell(blank),
            ],
            [
              buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.externo')),
              buildMedicionesPdfValueCell(blank),
              buildMedicionesPdfValueCell(blank),
            ],
          ],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  };

  const tensionBlock = {
    stack: [
      buildMedicionesPdfTitle(i18n('entities.tarea.medicionesParcial.tensionBobinado')),
      {
        table: {
          widths: [42, '*'],
          body: [
            [buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.inicio')), buildMedicionesPdfValueCell(blank)],
            [buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.termino')), buildMedicionesPdfValueCell(blank)],
          ],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  };

  const corrienteBlock = {
    stack: [
      buildMedicionesPdfTitle(i18n('entities.tarea.medicionesParcial.corrienteEstatica')),
      {
        table: {
          widths: [42, '*'],
          body: [
            [buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.inicio')), buildMedicionesPdfValueCell(blank)],
            [buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.termino')), buildMedicionesPdfValueCell(blank)],
          ],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  };

  const extraTextBlock = (titleKey: string) => ({
    stack: [
      buildMedicionesPdfTitle(i18n(titleKey)),
      {
        table: {
          widths: ['*'],
          body: [[buildMedicionesPdfValueCell(blank)]],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  });

  return {
    table: {
      widths: ['*', '*', '*', '*', 'auto'],
      body: [
        [
          extraTextBlock('entities.tarea.medicionesParcial.productoAFabricar'),
          extraTextBlock('entities.tarea.medicionesParcial.maquina'),
          extraTextBlock('entities.tarea.medicionesParcial.microperforado'),
          extraTextBlock('entities.tarea.medicionesParcial.colorFunda'),
          extraTextBlock('entities.tarea.medicionesParcial.soportaconos'),
        ],
        [
          termocontraccionBlock,
          cofBlock,
          tensionBlock,
          corrienteBlock,
          extraTextBlock('entities.tarea.medicionesParcial.pesoRollo'),
        ],
      ],
    },
    layout: medicionesInnerGridLayout,
    margin: [8, 8, 8, 8],
  };
};

const buildMedicionesCheckboxBox = (checked: boolean) => ({
  stack: [
    {text: checked ? '√' : ' ', margin: [1.1, 0, 0, -8.8], fontSize: 8},
    {
      canvas: [
        {
          type: 'polyline',
          lineWidth: 1,
          closePath: true,
          points: [
            {x: 0, y: 0},
            {x: 8, y: 0},
            {x: 8, y: 8},
            {x: 0, y: 8},
          ],
        },
      ],
    },
  ],
  width: 10,
  margin: [0, 1, 4, 1],
});

const buildMedicionesCheckboxList = (
  options: readonly string[],
  labels: Record<string, string>,
  selected: string[] | null | undefined,
) => ({
  columns: options.map((option) => ({
    columns: [
      buildMedicionesCheckboxBox(hasMedicionesParcialCheckbox(selected, option)),
      {
        text: labels[option] || option,
        fontSize: CHILD_TASKS_TABLE_FONT_SIZE,
        margin: [0, 2, 0, 0],
        width: '*',
      },
    ],
    margin: [0, 1, 0, 1],
  })),
  margin: [2, 2, 2, 2],
});

const buildMedicionesParcialId24PdfBlock = (_mediciones?: MedicionesParcialId24 | null) => {
  const blank = ' ';
  const controlVisualLabels = {
    INICIO: i18n('entities.tarea.medicionesParcial.inicio'),
    TERMINO: i18n('entities.tarea.medicionesParcial.termino'),
  };
  const tipoEmbalajeLabels = {
    SACO: i18n('entities.tarea.medicionesParcial.saco'),
    BOLSA_FUNDA: i18n('entities.tarea.medicionesParcial.bolsaFunda'),
  };

  const troquelBlock = {
    stack: [
      buildMedicionesPdfTitle(i18n('entities.tarea.medicionesParcial.troquel')),
      {
        table: {
          widths: ['*'],
          body: [[buildMedicionesPdfValueCell(blank)]],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  };

  const perforacionesBlock = {
    stack: [
      buildMedicionesPdfTitle(i18n('entities.tarea.medicionesParcial.cantPerforaciones')),
      {
        table: {
          widths: ['*'],
          body: [[buildMedicionesPdfValueCell(blank)]],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  };

  const controlVisualBlock = {
    stack: [
      buildMedicionesPdfTitle(i18n('entities.tarea.medicionesParcial.controlVisualGomaTeflon')),
      buildMedicionesCheckboxList(CONTROL_VISUAL_GOMA_TEFLON_OPTIONS, controlVisualLabels, []),
    ],
  };

  const embalajeBlock = {
    stack: [
      buildMedicionesPdfTitle(i18n('entities.tarea.medicionesParcial.tipoEmbalaje')),
      buildMedicionesCheckboxList(TIPO_EMBALAJE_OPTIONS, tipoEmbalajeLabels, []),
    ],
  };

  const extraTextBlock = (titleKey: string) => ({
    stack: [
      buildMedicionesPdfTitle(i18n(titleKey)),
      {
        table: {
          widths: ['*'],
          body: [[buildMedicionesPdfValueCell(blank)]],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  });

  /*const microPrepicadoBlock = {
    table: {
      widths: ['*', 'auto'],
      body: [
        [
          extraTextBlock('entities.tarea.medicionesParcial.microperforado'),
          extraTextBlock('entities.tarea.medicionesParcial.prepicado'),
        ],
      ],
    },
    layout: microperforadoInnerGridLayout,
  };*/

  return {
    table: {
      widths: ['*', '*', '*', '*', 'auto'],
      body: [
        [
          troquelBlock,
          perforacionesBlock,
          extraTextBlock('entities.tarea.medicionesParcial.maquinaSelladora'),
          extraTextBlock('entities.tarea.medicionesParcial.microperforado'),
          extraTextBlock('entities.tarea.medicionesParcial.prepicado'),
        ],
        [
          controlVisualBlock,
          embalajeBlock,
          extraTextBlock('entities.tarea.medicionesParcial.tipoSello'),
          extraTextBlock('entities.tarea.medicionesParcial.colorFunda'),
          '',
        ],
      ],
    },
    layout: medicionesInnerGridLayout,
    margin: [8, 8, 8, 8],
  };
};

const buildValidatorSignatureFooterTable = async (params: {
  tenant: any;
  settings: any;
  parcial: any | null;
  observaciones?: any[];
}) => {
  const companyRut = params.settings?.appFields?.childTasksTableView?.companyRut || '';

  const infoLineStyle = {
    fontSize: CHILD_TASKS_TABLE_FONT_SIZE,
    alignment: 'center' as const,
    color: PDF_TABLE_BORDER_COLOR,
  };

  const signatureBlock = {
    verticalAlignment: 'bottom' as const,
    stack: [
      ...(companyRut
        ? [
            {
              text: i18n('entities.tarea.validatorSignatureRut', companyRut),
              ...infoLineStyle,
            },
          ]
        : []),
      {
        alignment: 'center',
        stack: [
          {text: ' ', margin: [0, 18, 0, 1]},
          // Plantilla en blanco: reservar nombre y cargo justo encima de la línea.
          {text: ' ', ...infoLineStyle, margin: [0, 2, 0, 0]},
          {text: ' ', ...infoLineStyle},
          {
            columns: [
              {width: '*', text: ''},
              {
                width: 150,
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 150, y2: 0, lineWidth: 0.6}],
              },
              {width: '*', text: ''},
            ],
            columnGap: 0,
            margin: [0, 2, 0, 1],
          },
          {
            text: i18n('entities.tarea.validatorSignatureLabel'),
            fontSize: 6.5,
            color: '#666666',
            alignment: 'center',
          },
        ],
      },
    ],
    margin: [8, 6, 12, 2],
  };

  // Observaciones vacías pero con el título de sección para conservar estructura.
  const observacionesBlock = {
    stack: [
      {
        text: i18n('entities.tarea.answerTableObservaciones'),
        fontSize: 6.5,
        color: '#666666',
        margin: [0, 0, 0, 4],
      },
      {text: ' ', fontSize: CHILD_TASKS_TABLE_FONT_SIZE},
      {text: ' ', fontSize: CHILD_TASKS_TABLE_FONT_SIZE, margin: [0, 8, 0, 0]},
      {text: ' ', fontSize: CHILD_TASKS_TABLE_FONT_SIZE, margin: [0, 8, 0, 0]},
    ],
    margin: [8, 8, 8, 8],
  };

  return {
    unbreakable: true,
    table: {
      widths: ['*', 210],
      body: [[observacionesBlock, signatureBlock]],
    },
    layout: parentAnswersTableLayout,
    margin: [0, 12, 0, 0],
  };
};

const buildChildTasksPdfHeader = (params: {
  logo: string | null;
  tenantName: string;
  title: string;
  date: string;
  codeMeta?: PlastyvergPdfHeaderCodeMeta | null;
}) => {
  // Márgenes compactos; la fila parte del mínimo y crece con logo / fecha+versión / título.
  const cellVerticalMargin = Math.max(2, Math.round((PDF_HEADER_ROW_MIN_HEIGHT - 16) / 2));
  const rightCell = params.codeMeta
    ? buildPlastyvergHeaderCodeCard(params.codeMeta, cellVerticalMargin)
    : buildHeaderDateCard(params.date, cellVerticalMargin);

  // pdfMake alinea celdas al tope; aproximamos centro vertical del título según hermano más alto.
  const titleFontSize = 13;
  const titleLineHeight = Math.round(titleFontSize * 1.25);
  const titleText = String(params.title || '');
  const estimatedTitleLines = Math.max(1, titleText.split('\n').length, titleText.length > 55 ? 2 : 1);
  const estimatedTitleHeight = estimatedTitleLines * titleLineHeight;
  const estimatedRightHeight = params.codeMeta ? 50 : 30;
  const estimatedLogoHeight = params.logo ? 46 : 0;
  const estimatedRowHeight = Math.max(PDF_HEADER_ROW_MIN_HEIGHT, estimatedRightHeight, estimatedLogoHeight);
  const titleTopMargin = Math.max(2, Math.round((estimatedRowHeight - estimatedTitleHeight) / 2));

  return {
    layout: parentAnswersTableLayout,
    table: {
      widths: ['auto', '*', 'auto'],
      // Altura mínima; si el contenido (logo, Version, etc.) necesita más, la fila crece.
      heights: [PDF_HEADER_ROW_MIN_HEIGHT],
      body: [
        [
          params.logo
            ? {image: params.logo, maxWidth: 100, maxHeight: 40, alignment: 'center', margin: [12, 6, 4, 2]}
            : {
                text: params.tenantName,
                fontSize: 9,
                alignment: 'center',
                margin: [4, cellVerticalMargin, 4, cellVerticalMargin],
              },
          {
            text: params.title,
            fontSize: titleFontSize,
            bold: true,
            alignment: 'center',
            margin: [8, titleTopMargin, 8, 2],
          },
          rightCell,
        ],
      ],
    },
    margin: [0, 0, 0, 12],
  };
};

/**
 * Padding de celdas en pdfMake.
 * `top`/`bottom` aplican a todas las filas (incluye cabecera).
 * `bodyExtraTop`/`bodyExtraBottom` se suman solo en filas de cuerpo.
 */
const TABLE_CELL_PADDING = {
  left: 1,
  right: 1.5,
  top: 1,
  bottom: 1,
  bodyExtraTop: 1,
  bodyExtraBottom: 17,
};

/** Línea vertical de la grilla (pdfMake la suma fuera de `widths`). */
const TABLE_VLINE_WIDTH = 0.35;

const toColumnPoints = (value: number) => Math.floor(value * 100) / 100;

const getTableOffsetsTotal = (columnCount: number) =>
  columnCount * (TABLE_CELL_PADDING.left + TABLE_CELL_PADDING.right) + (columnCount + 1) * TABLE_VLINE_WIDTH;

/** Parte de `floors`; el resto del ancho útil sube hacia `fulls`, primero las palabras más cortas. Si aún sobra, se reparte a partes iguales hasta llenar la página. */
const fillTowardFull = (floors: number[], fulls: number[], targetWidth: number) => {
  const widths = floors.map((width) => Math.max(width, 1));
  const budget = Math.max(targetWidth - getTableOffsetsTotal(widths.length), widths.length);
  let leftover = budget - widths.reduce((sum, width) => sum + width, 0);
  if (leftover <= 0) return widths.map((width) => toColumnPoints(width));
  const order = fulls.map((_, index) => index).sort((a, b) => fulls[a] - fulls[b]);
  for (const index of order) {
    if (leftover <= 0) break;
    const room = Math.max(0, fulls[index] - widths[index]);
    const take = Math.min(room, leftover);
    widths[index] += take;
    leftover -= take;
  }
  if (leftover > 0) {
    const share = leftover / widths.length;
    for (let index = 0; index < widths.length; index += 1) widths[index] += share;
  }
  const rounded = widths.map((width) => toColumnPoints(width));
  const drift = budget - rounded.reduce((sum, width) => sum + width, 0);
  if (rounded.length > 0 && Math.abs(drift) >= 0.01) {
    rounded[rounded.length - 1] = toColumnPoints(rounded[rounded.length - 1] + drift);
  }
  return rounded;
};

const resolveTableLayout = (columnCount: number) => {
  if (columnCount > CHILD_TASKS_WIDE_TABLE_COLUMN_THRESHOLD) return {signatureWidth: 56, signatureFit: [52, 24]};
  if (columnCount >= 14) return {signatureWidth: 64, signatureFit: [60, 28]};
  if (columnCount >= 10) return {signatureWidth: 72, signatureFit: [68, 30]};
  if (columnCount >= 7) return {signatureWidth: 80, signatureFit: [76, 34]};
  return {signatureWidth: 90, signatureFit: [86, 38]};
};

const getUsablePageWidth = (
  pageOrientation: 'portrait' | 'landscape',
  pageSize: ChildTasksPdfPageSize = 'A4',
  horizontalMargins = PAGE_MARGINS.left + PAGE_MARGINS.right,
) => getPageDimensions(pageSize, pageOrientation).width - horizontalMargins;

const getChildTableRowNumber = (task: any, index: number, useSequentialNumber: boolean) =>
  useSequentialNumber ? index + 1 : getTaskDisplayNumber(task, index + 1);

const APRUEBA_RECHAZA_OBS_HEADER_PATTERN = /^\(A\)\s*APRUEBA\s*\(R\)\s*RECHAZA\s*\(\*\)\s*OBSERVACI[OÓ]N$/i;
const APRUEBA_RECHAZA_OBS_LINES = ['(A) APRUEBA', '(R) RECHAZA', '(*) OBSERVACIÓN'] as const;
const APRUEBA_RECHAZA_OBS_MIN_WIDTH_FACTOR = 0.9;
/**
 * Resta este valor × letras de la palabra más larga al factor 1.
 * 0.01 y 10 letras → piso 0.9; el hueco se reparte hacia Helvetica, palabras cortas primero.
 */
const COLUMN_WORD_CHAR_WIDTH_PENALTY = 0.05;

const longestWordCharCount = (text: string) => {
  const words = String(text || '')
    .split(/\s+/)
    .map((word) => word.trim())
    .filter(Boolean);
  return words.length === 0 ? 0 : Math.max(...words.map((word) => word.length));
};

const wordFloor = (full: number, ...texts: string[]) => {
  const chars = Math.max(0, ...texts.map(longestWordCharCount));
  return full * Math.max(0, 1 - COLUMN_WORD_CHAR_WIDTH_PENALTY * chars);
};

const normalizeHeaderLabelForMatch = (label: string) =>
  String(label || '')
    .replace(/\s+/g, ' ')
    .trim();

const isApruebaRechazaObservacionHeader = (label: string) =>
  APRUEBA_RECHAZA_OBS_HEADER_PATTERN.test(normalizeHeaderLabelForMatch(label));

const headerWordFull = (label: string) => {
  if (isApruebaRechazaObservacionHeader(label)) {
    const lineWidth = Math.max(
      ...APRUEBA_RECHAZA_OBS_LINES.map((line) => helveticaStringWidth(line, true, CHILD_TASKS_TABLE_FONT_SIZE)),
    );
    return lineWidth * APRUEBA_RECHAZA_OBS_MIN_WIDTH_FACTOR;
  }
  return helveticaWidestWord(label, true, CHILD_TASKS_TABLE_FONT_SIZE);
};

const buildQuestionHeaderCell = (label: string) => {
  if (isApruebaRechazaObservacionHeader(label)) {
    return {
      stack: APRUEBA_RECHAZA_OBS_LINES.map((line) => ({
        text: line,
        bold: true,
        noWrap: true,
        alignment: 'center' as const,
      })),
      alignment: 'center' as const,
    };
  }
  return {text: label, bold: true, alignment: 'center' as const};
};

const buildContentFitColumnWidths = (
  columns: any[],
  rows: any[],
  _minSignatureWidth: number,
  pageOrientation: 'portrait' | 'landscape',
  headerLabels: {number: string; signature: string},
  useSequentialRowNumbers = false,
  pageSize: ChildTasksPdfPageSize = 'A4',
  targetWidth?: number,
  includeSignature = true,
  tenantId?: string | null,
) => {
  const usableWidth = targetWidth ?? getUsablePageWidth(pageOrientation, pageSize);
  const bodyWordFull = (text: string) => helveticaWidestWord(text, false, CHILD_TASKS_TABLE_FONT_SIZE);

  const numberTexts = [
    headerLabels.number,
    ...rows.map((row, index) => String(getChildTableRowNumber(row, index, useSequentialRowNumbers))),
  ];
  const numberFull = Math.max(
    headerWordFull(headerLabels.number),
    ...rows.map((row, index) => bodyWordFull(String(getChildTableRowNumber(row, index, useSequentialRowNumbers)))),
  );
  const numberFloor = wordFloor(numberFull, ...numberTexts);
  const signatureFull = headerWordFull(headerLabels.signature);
  const signatureFloor = wordFloor(signatureFull, headerLabels.signature);

  if (columns.length === 0) {
    return fillTowardFull(
      includeSignature ? [numberFloor, signatureFloor] : [numberFloor],
      includeSignature ? [numberFull, signatureFull] : [numberFull],
      usableWidth,
    );
  }

  const questionFulls = columns.map((column) => {
    const headerFull = headerWordFull(column.label);
    const bodyFull = Math.max(
      0,
      ...rows.map((row) => bodyWordFull(getParcialAnswerText(row, column, '-', {includeAgregados: false, tenantId}))),
    );
    return Math.max(headerFull, bodyFull);
  });
  const questionFloors = columns.map((column, index) =>
    isApruebaRechazaObservacionHeader(column.label)
      ? questionFulls[index]
      : wordFloor(
          questionFulls[index],
          column.label,
          ...rows.map((row) => getParcialAnswerText(row, column, '-', {includeAgregados: false, tenantId})),
        ),
  );

  const floors = includeSignature ? [numberFloor, ...questionFloors, signatureFloor] : [numberFloor, ...questionFloors];
  const fulls = includeSignature ? [numberFull, ...questionFulls, signatureFull] : [numberFull, ...questionFulls];
  return fillTowardFull(floors, fulls, usableWidth);
};

const buildChildTasksTableLayout = (headerRowCount: number) => ({
  hLineWidth: (i) => (i === 0 ? 0.7 : 0.35),
  vLineWidth: () => TABLE_VLINE_WIDTH,
  hLineColor: () => '#b8b8b8',
  vLineColor: () => PDF_TABLE_BORDER_COLOR,
  paddingLeft: () => TABLE_CELL_PADDING.left,
  paddingRight: () => TABLE_CELL_PADDING.right,
  // pdfMake pasa (rowIndex, node); el extra vertical solo aplica fuera de cabecera.
  paddingTop: (rowIndex) => TABLE_CELL_PADDING.top + (rowIndex < headerRowCount ? 0 : TABLE_CELL_PADDING.bodyExtraTop),
  paddingBottom: (rowIndex) => TABLE_CELL_PADDING.bottom + (rowIndex < headerRowCount ? 0 : TABLE_CELL_PADDING.bodyExtraBottom),
});

const invisibleGridLayout = {
  hLineWidth: () => 0,
  vLineWidth: () => 0,
  paddingLeft: () => 2,
  paddingRight: () => 2,
  paddingTop: () => 0,
  paddingBottom: () => 4,
};

const BLANK_TABLE_ROW_HEIGHT_PT = 11.5 + TABLE_CELL_PADDING.bodyExtraTop + TABLE_CELL_PADDING.bodyExtraBottom;

const buildBlankPdfCriteriaSection = (items: ParcialCriteriaItem[]) => ({
  table: {
    widths: ['*'],
    body: [
      [
        {
          stack: items.map((item, index) => ({
            text: [{text: `${item.title} `, bold: true}, {text: item.body}],
            fontSize: CHILD_TASKS_TABLE_FONT_SIZE,
            margin: [0, index === 0 ? 0 : 3, 0, 0],
          })),
          margin: [8, 8, 8, 8],
        },
      ],
    ],
  },
  layout: parentAnswersTableLayout,
  margin: [0, 10, 0, 0],
});

/**
 * Estima cuántas filas en blanco caben en la hoja sin empujar el footer de firma
 * a la siguiente página. Es una estimación conservadora (pt de pdfMake).
 */
const estimateBlankBodyRowCount = (params: {
  pageSize: ChildTasksPdfPageSize;
  pageOrientation: 'portrait' | 'landscape';
  parentGridRowCount: number;
  showGroupTitles: boolean;
  useSubsectionHeaders: boolean;
  showMedicionesColumn: boolean;
  fullWidthGroupCount: number;
  sideBySidePairCount: number;
}) => {
  const page = getPageDimensions(params.pageSize, params.pageOrientation);
  const usableHeight = page.height - PAGE_MARGINS.top - PAGE_MARGINS.bottom;

  const parentHeight = params.parentGridRowCount > 0 ? params.parentGridRowCount * 34 + 12 : 0;
  const groupTitleBudget = params.showGroupTitles ? params.fullWidthGroupCount * 22 : 8;
  const tableHeaderHeight = params.useSubsectionHeaders ? 28 : 14;
  const criteriaSectionHeight = 78;
  const footerHeight = 118;
  const medicionesSectionHeight = params.showMedicionesColumn ? 100 : 0;
  const sideBySideBudget = params.sideBySidePairCount * 90;
  const multiTableGap = Math.max(0, params.fullWidthGroupCount - 1) * 16;
  const safetyBuffer = 24;

  const reserved =
    parentHeight +
    groupTitleBudget +
    tableHeaderHeight * Math.max(1, params.fullWidthGroupCount) +
    criteriaSectionHeight +
    medicionesSectionHeight +
    footerHeight +
    sideBySideBudget +
    multiTableGap +
    safetyBuffer;

  const remaining = usableHeight - reserved;
  const rows = Math.floor(remaining / BLANK_TABLE_ROW_HEIGHT_PT);
  return Math.max(8, rows);
};

const buildChildGroupTableBlock = async (params: {
  grupo: {modelo?: any; inspecciones: any[]};
  columns: any[];
  showTitle: boolean;
  useSequentialRowNumbers: boolean;
  pageOrientation: 'portrait' | 'landscape';
  pageSize: ChildTasksPdfPageSize;
  targetWidth?: number;
  showSubsectionHeaders?: boolean;
  blankRowCount: number;
  includeSignatureColumn: boolean;
  applyPdfFormatChanges?: boolean;
  tenantId?: string | null;
}) => {
  const {
    grupo,
    columns,
    showTitle,
    useSequentialRowNumbers,
    pageOrientation,
    pageSize,
    targetWidth,
    showSubsectionHeaders = false,
    blankRowCount,
    includeSignatureColumn,
    applyPdfFormatChanges = false,
    tenantId,
  } = params;
  const layout = resolveTableLayout(columns.length);
  const headerLabels = {
    number: i18n('entities.firma.parcial.number'),
    signature: getExecutorSignatureColumnLabel(tenantId),
  };

  const includeSignature = includeSignatureColumn;
  const useSubsectionHeaders = showSubsectionHeaders && hasSubsectionHeaderSpans(columns);
  const subsectionSpans = useSubsectionHeaders ? buildSubsectionHeaderSpans(columns) : [];

  const questionHeaderCells = columns.map((c) => buildQuestionHeaderCell(c.label));
  const centeredCell = applyPdfFormatChanges ? {alignment: 'center' as const} : {};
  const numberHeaderCell = {text: headerLabels.number, bold: true, ...centeredCell};
  const signatureHeaderCell = includeSignature ? [{text: headerLabels.signature, bold: true, ...centeredCell}] : [];

  let headerRows: any[][];
  if (useSubsectionHeaders) {
    const subsectionHeaderCells = subsectionSpans.flatMap((span) => [
      {
        text: span.label,
        bold: true,
        alignment: 'center' as const,
        colSpan: span.colSpan,
      },
      ...Array.from({length: Math.max(0, span.colSpan - 1)}, () => ({})),
    ]);

    headerRows = [
      [
        {...numberHeaderCell, rowSpan: 2},
        ...subsectionHeaderCells,
        ...(includeSignature ? [{text: headerLabels.signature, bold: true, rowSpan: 2, ...centeredCell}] : []),
      ],
      [{}, ...questionHeaderCells, ...(includeSignature ? [{}] : [])],
    ];
  } else {
    headerRows = [[numberHeaderCell, ...questionHeaderCells, ...signatureHeaderCell]];
  }

  // Filas en blanco: número de fila visible, celdas de respuesta/firma vacías.
  const bodyRows = Array.from({length: blankRowCount}, (_, index) => [
    {text: String(index + 1), ...centeredCell},
    ...columns.map(() => ({text: ' ', ...centeredCell})),
    ...(includeSignature ? [{text: ' ', ...centeredCell}] : []),
  ]);

  // Anchos basados en headers + filas reales (misma estructura visual que el PDF lleno).
  const widths = buildContentFitColumnWidths(
    columns,
    grupo.inspecciones,
    layout.signatureWidth,
    pageOrientation,
    headerLabels,
    useSequentialRowNumbers,
    pageSize,
    targetWidth,
    includeSignature,
    tenantId,
  );

  const stack: any[] = [];
  if (showTitle) {
    stack.push({
      text: grupo.modelo?.titulo || i18n('entities.tarea.answerTableUntypedGroup'),
      style: 'groupTitle',
      margin: [0, 0, 0, 4],
    });
  }

  stack.push({
    table: {
      headerRows: useSubsectionHeaders ? 2 : 1,
      widths,
      body: [...headerRows, ...bodyRows],
    },
    layout: buildChildTasksTableLayout(useSubsectionHeaders ? 2 : 1),
    fontSize: CHILD_TASKS_TABLE_FONT_SIZE,
  });

  return {stack, margin: [0, showTitle ? 8 : 0, 0, 0]};
};

/**
 * Exporta una plantilla PDF vacía con la misma estructura del PDF tabulado:
 * labels de cabecera/padre, headers de columnas hijas, filas en blanco numeradas
 * (suficientes para llenar la hoja sin empujar la firma) y pie de firma vacío.
 */
export async function exportChildTasksTableBlankPdf(
  params: {
    parentTask: any;
    tasks: any[];
    parentQuestionKeys: string[] | ParentFieldLayoutItem[] | ParentFieldLayoutConfig;
    title?: string;
  } & ChildTasksTablePdfParcialSource,
  options: {pageOrientation?: 'portrait' | 'landscape'} = {},
) {
  let {pageOrientation = 'landscape'} = options;
  let pageSize: ChildTasksPdfPageSize = 'A4';
  const parent = params.parentTask;
  const tasks = params.tasks || [];
  if (!tasks.length) {
    throw new Error('exportChildTasksTableBlankPdf requires at least one task (synthetic or real)');
  }
  const parentDisplayNumber = getTaskReportFolio(parent);
  const parentTitle = parent?.tituloTarea || parent?.titulo || parent?.modeloTarea?.titulo || '';
  const grupos = groupTasksByModelo(tasks);
  const singleModelGroup = grupos.length === 1 ? grupos[0] : null;
  const headerTitle = singleModelGroup
    ? singleModelGroup.modelo?.titulo || tasks[0]?.tituloTarea || tasks[0]?.titulo || parentTitle
    : parentTitle;
  const displayTitle = singleModelGroup
    ? headerTitle
    : parentDisplayNumber
      ? `${parentDisplayNumber} - ${headerTitle}`
      : headerTitle;

  const {tenant, logo} = await fetchTenantLogo();
  const settings = await SettingsService.find();
  const applyPdfFormatChanges = shouldApplyPartialPdfFormatChanges(tenant?.id);
  const {displayTitle: pdfHeaderTitle, codeMeta: pdfHeaderCodeMeta} = resolvePlastyvergPdfHeaderTitle(
    displayTitle,
    isPlastyvergTenant(tenant?.id),
  );

  const groupColumns = grupos.map((grupo) => buildParcialColumns(grupo.inspecciones));
  const preparedGroups = grupos
    .map((grupo, index) => ({grupo, columns: groupColumns[index]}))
    .filter((item) => item.columns.length > 0)
    .sort((left, right) => right.columns.length - left.columns.length);
  if (!preparedGroups.length) {
    throw new Error('exportChildTasksTableBlankPdf: no procedure columns to render');
  }
  const maxColumnCount = preparedGroups.reduce((max, item) => Math.max(max, item.columns.length), 0);
  const useWideTablePage = isChildTasksWideTableMode(tenant?.id, maxColumnCount);

  if (useWideTablePage) {
    pageOrientation = 'landscape';
    pageSize = 'A3';
  } else if (isPlastyvergTenant(tenant?.id)) {
    pageSize = PAGE_SIZE_PLASTYVERG;
  }

  const pageHeader = buildChildTasksPdfHeader({
    logo,
    tenantName: tenant?.name || '',
    title: pdfHeaderTitle,
    date: pdfHeaderCodeMeta?.fechaVersion || BLANK_PDF_FIXED_DATE,
    codeMeta: pdfHeaderCodeMeta,
  });

  const content: any[] = [];

  const parentGrid = buildParentAnswerGrid(parent, params.parentQuestionKeys);
  if (parentGrid.body.length > 0) {
    content.push({
      table: {
        widths: Array(parentGrid.cellsPerRow).fill('*'),
        body: parentGrid.body,
      },
      layout: parentAnswersTableLayout,
      margin: [0, 0, 0, 12],
    });
  }

  const useSequentialRowNumbers = shouldUseSequentialChildTableRowNumbers(preparedGroups.length, tenant?.id);
  const showGroupTitles = preparedGroups.length > 1;
  const usablePageWidth = getUsablePageWidth(pageOrientation, pageSize);
  const sideBySideTargetWidth = (usablePageWidth - 8) / 2;

  const fullWidthGroups = useWideTablePage
    ? preparedGroups.filter((item) => item.columns.length > SIDE_BY_SIDE_MAX_COLUMNS)
    : preparedGroups;
  const sideBySideGroups = useWideTablePage
    ? preparedGroups.filter((item) => item.columns.length <= SIDE_BY_SIDE_MAX_COLUMNS)
    : [];

  const primeroDelParcialId23Task = isPlastyvergTenant(tenant?.id) ? findPrimeroDelParcialId23Task(tasks) : null;
  const primeroDelParcialId24Task = isPlastyvergTenant(tenant?.id) ? findPrimeroDelParcialId24Task(tasks) : null;
  const showMedicionesId23 = Boolean(
    primeroDelParcialId23Task &&
    shouldUseMedicionesParcialId23({
      tenantId: tenant?.id,
      modeloTareaTitulo: primeroDelParcialId23Task?.modeloTarea?.titulo,
      primeroDelParcial: primeroDelParcialId23Task?.primeroDelParcial,
    }),
  );
  const showMedicionesId24 = Boolean(
    !showMedicionesId23 &&
    primeroDelParcialId24Task &&
    shouldUseMedicionesParcialId24({
      tenantId: tenant?.id,
      modeloTareaTitulo: primeroDelParcialId24Task?.modeloTarea?.titulo,
      primeroDelParcial: primeroDelParcialId24Task?.primeroDelParcial,
    }),
  );
  const showMedicionesColumn = showMedicionesId23 || showMedicionesId24;
  const medicionesKind = showMedicionesId24 ? 'id24' : showMedicionesId23 ? 'id23' : null;

  const blankRowCount = estimateBlankBodyRowCount({
    pageSize,
    pageOrientation,
    parentGridRowCount: parentGrid.body.length,
    showGroupTitles,
    useSubsectionHeaders: useWideTablePage,
    showMedicionesColumn,
    fullWidthGroupCount: Math.max(1, fullWidthGroups.length),
    sideBySidePairCount: Math.ceil(sideBySideGroups.length / 2),
  });

  // Si hay varias tablas a ancho completo, repartir filas para no desbordar la hoja.
  const rowsPerFullWidthGroup = 5;
  //fullWidthGroups.length > 1 ? Math.max(4, Math.floor(blankRowCount / fullWidthGroups.length)) : blankRowCount;
  const sideBySideBlankRows = Math.max(4, Math.min(8, Math.floor(blankRowCount / 3)));

  // En plantilla en blanco la columna de firma siempre se muestra (celdas vacías).
  const includeSignatureColumn = true;

  for (const {grupo, columns} of fullWidthGroups) {
    content.push(
      await buildChildGroupTableBlock({
        grupo,
        columns,
        showTitle: showGroupTitles,
        useSequentialRowNumbers,
        pageOrientation,
        pageSize,
        showSubsectionHeaders: useWideTablePage,
        blankRowCount: rowsPerFullWidthGroup,
        includeSignatureColumn,
        applyPdfFormatChanges,
        tenantId: tenant?.id,
      }),
    );
  }

  for (let index = 0; index < sideBySideGroups.length; index += 2) {
    const left = sideBySideGroups[index];
    const right = sideBySideGroups[index + 1];
    const leftBlock = await buildChildGroupTableBlock({
      grupo: left.grupo,
      columns: left.columns,
      showTitle: true,
      useSequentialRowNumbers,
      pageOrientation,
      pageSize,
      targetWidth: sideBySideTargetWidth,
      showSubsectionHeaders: useWideTablePage,
      blankRowCount: sideBySideBlankRows,
      includeSignatureColumn,
      applyPdfFormatChanges,
      tenantId: tenant?.id,
    });

    if (!right) {
      content.push({
        ...leftBlock,
        margin: [0, 8, 0, 0],
      });
      continue;
    }

    const rightBlock = await buildChildGroupTableBlock({
      grupo: right.grupo,
      columns: right.columns,
      showTitle: true,
      useSequentialRowNumbers,
      pageOrientation,
      pageSize,
      targetWidth: sideBySideTargetWidth,
      showSubsectionHeaders: useWideTablePage,
      blankRowCount: sideBySideBlankRows,
      includeSignatureColumn,
      applyPdfFormatChanges,
      tenantId: tenant?.id,
    });

    content.push({
      table: {
        widths: ['*', '*'],
        body: [
          [
            {stack: leftBlock.stack, margin: [0, 0, 4, 0]},
            {stack: rightBlock.stack, margin: [4, 0, 0, 0]},
          ],
        ],
      },
      layout: invisibleGridLayout,
      margin: [0, 8, 0, 0],
    });
  }

  const criteriaTitulo = resolveParcialCriteriaTitulo({parentTask: parent, tasks});
  const criteriaItems = getPlastyvergParcialCriteriaItems(criteriaTitulo);
  if (criteriaItems?.length) {
    content.push(buildBlankPdfCriteriaSection(criteriaItems));
  }

  if (showMedicionesColumn) {
    content.push(
      wrapMedicionesPdfSection(
        medicionesKind === 'id24' ? buildMedicionesParcialId24PdfBlock(null) : buildMedicionesParcialId23PdfBlock(null),
      ),
    );
  }

  content.push(
    await buildValidatorSignatureFooterTable({
      tenant,
      settings,
      parcial: null,
      observaciones: [],
    }),
  );

  const definition = {
    content,
    pageSize: toPdfMakePageSize(pageSize),
    pageOrientation,
    defaultStyle: {fontSize: 8},
    styles: {
      title: {fontSize: 16, bold: true},
      subtitle: {fontSize: 12, color: '#666666'},
      groupTitle: {fontSize: 11, bold: true},
    },
    pageMargins: [PAGE_MARGINS.left, PAGE_MARGINS.top, PAGE_MARGINS.right, PAGE_MARGINS.bottom],
    header: () => ({
      ...pageHeader,
      margin: [PAGE_MARGINS.left, PAGE_HEADER_MARGIN.top, PAGE_MARGINS.right, PAGE_HEADER_MARGIN.bottom],
    }),
    footer: (currentPage, pageCount) => ({
      margin: [PAGE_MARGINS.left, PAGE_FOOTER_MARGIN.top, PAGE_MARGINS.right, PAGE_FOOTER_MARGIN.bottom],
      columns: [
        {
          text: `Página ${currentPage} de ${pageCount}`,
          alignment: 'left',
          color: '#303030',
          fontSize: 7,
        },
        {
          margin: [0, 3, 0, 0],
          text: 'Generado con planiwise.com',
          alignment: 'right',
          color: '#A0A0A0',
          fontSize: 6,
        },
      ],
    }),
  };

  pdfMake.createPdf(definition as any).open();
}

const tareaParcialBlankPdf = {exportChildTasksTableBlankPdf};

export default tareaParcialBlankPdf;
